import React, { useEffect, useState } from "react";

import RPMDeviceInput from "./form/RPMDeviceInput";
import PatientEnrollForm from "./form/PatientEnrollForm";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";

import formatDate from "utils/formatDate";
import { showErrorToast, showSuccessToast } from "utils/toast";

// Import CSS
import "components/common/modals/patientEnrollModal/PatientEnrollModal.css";
import { Formik } from "formik";

import * as Yup from "yup";
import { Button } from "@mui/material";
import SubmitButton from "components/Button/SubmitButton";
import { isValidDate } from "utils/date";
import { DEFAULT_NOTIFICATION_CHANNEL } from "static/constants";

const PatientEnrollModal = (props) => {
  const {
    modalOpen,
    closeModal,
    role,
    enrollPatientSubmit,
    allowInitialRPMModal = true,
    getPatients,
  } = props;

  const [loading, setLoading] = useState(false);

  const rpmDeviceModel = { imei: "", active: true, rpmDeviceName: "" };

  const patientModel = {
    firstName: "",
    lastName: "",
    account: "",
    dob: "",
    timezone: "",
    gender: "",
    active: true,
    genderIdentity: "",
    sexAssignedAtBirth: "",
    manufacturer: "",
    modelNumber: "",
    modelName: "",
    activeDevice: true,
    rpm_devices: allowInitialRPMModal ? [rpmDeviceModel] : [],
    subscribe_setting: {
      // set all channels to true by default
      notification_channels: DEFAULT_NOTIFICATION_CHANNEL,
      letter: false,
      subscribe: false,
      next_send: false,
      first_call: false,
      third_call: false,
      second_call: false,
    },
  };

  const [payload, setPayload] = useState({ ...patientModel });

  useEffect(() => {
    const clinicName = localStorage.getItem("clinicName");
    if (clinicName !== "None") {
      const payloadCopy = Object.assign(payload, {});
      payloadCopy["account"] = clinicName;
      setPayload({ ...payloadCopy });
    }
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Yup validation schema
  const validationSchema = (payload) => {
    return Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      account: Yup.string().required("Account is required"),
      dob: Yup.date().required("Date of birth is required").nullable(),
      doi:
        payload?.rpm_devices?.length === 0
          ? Yup.string()
              .required("Date of Implant is required")
              .test(
                "is-valid-date",
                "Date of Implant must be a valid date",
                (value) => isValidDate(value)
              )
          : Yup.string().optional(),
      timezone: Yup.string().required("Timezone is required"),
      gender: Yup.string().label("Gender"),
      ...(allowInitialRPMModal
        ? {}
        : {
            manufacturer: Yup.string().required("Manufacturer is required"),
            modelName: Yup.string().required("Model name is required"),
            modelNumber: Yup.string().required("Model number is required"),
          }),
      rpm_devices: Yup.array().of(
        Yup.object().shape({
          imei: Yup.string().required("IMEI is required"),
          rpmDeviceName: Yup.string().required("Device name is required"),
        })
      ),
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const payloadCopy = Object.assign(values, {});
      const formattedDate = formatDate(payloadCopy.dob, "MM/DD/YYYY");
      payloadCopy.dob = formattedDate;

      const response = await enrollPatientSubmit(payloadCopy);

      if (response?.type === "Success") {
        handleClose();
        showSuccessToast(response?.message);
        getPatients && getPatients();
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal();
    const payloadCopy = Object.assign(patientModel, {});
    setPayload({ ...payloadCopy });
  };

  return (
    <ModalWrapper
      title="Enroll New Patient"
      show={modalOpen}
      showCloseIcon={true}
      headerStyle={{ padding: "0.25rem 1.5rem" }}
      maxWidth="md"
      onClose={(_event, reason) => {
        if (reason && reason === "backdropClick") return;
        handleClose();
      }}
    >
      <Formik
        initialValues={{ ...payload }}
        validationSchema={validationSchema(payload)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          isSubmitting,
          setFieldValue,
          errors,
          touched,
          handleSubmit: onFormSubmit,
        }) => {
          return (
            <>
              <PatientEnrollForm
                role={role}
                isLoading={isSubmitting}
                patientData={values}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
                allowInitialRPMModal={allowInitialRPMModal}
              />

              <div
                className="d-flex align-items-center flex-end mt-2"
                style={{ gap: "10px" }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <SubmitButton
                  buttonText={isSubmitting ? "Adding..." : "Add Patient"}
                  handleClick={onFormSubmit}
                  isLoading={loading}
                />
              </div>
            </>
          );
        }}
      </Formik>
    </ModalWrapper>
  );
};

export default PatientEnrollModal;
